import { Box, LinearProgress, Typography, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import { useTenantTranslation } from "hooks/formatters";

type StorageProgressBarProps = Readonly<{
  usedGb: number | null;
  totalGb: number | null;
  isFetching: boolean;
}>;

export const StorageProgressBar = ({
  usedGb,
  totalGb,
  isFetching,
}: StorageProgressBarProps) => {
  const theme = useTheme();
  const { t } = useTenantTranslation();
  const isUnknown = usedGb == null || totalGb == null;
  const percent = isUnknown ? 0 : (usedGb / totalGb) * 100;

  const getStorageColor = (percent: number) => {
    if (percent >= 90) return theme.palette.error.main;
    if (percent >= 75) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "bottom",
          justifyContent: "space-between",
          gap: 1,
          mb: 0,
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          💾 {t("storageUsed")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("storageProgress", {
            used: usedGb?.toFixed(1),
            total: totalGb?.toFixed(1),
            percent: isUnknown ? "?" : percent.toFixed(1),
          })}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Box sx={{ width: "100%", mr: 1 }}>
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            style={{ transformOrigin: "left" }}
          >
            <LinearProgress
              variant={
                isUnknown && isFetching ? "indeterminate" : "determinate"
              }
              value={isFetching ? 1 : isUnknown ? 0 : percent}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: theme.palette.grey[200],
                "& .MuiLinearProgress-bar": {
                  backgroundColor: isUnknown
                    ? theme.palette.info.main
                    : getStorageColor(percent),
                  borderRadius: 4,
                },
              }}
            />
          </motion.div>
        </Box>
      </Box>
    </Box>
  );
};

export default StorageProgressBar;
